import { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";

import { useSystemMeta } from "../../hooks/useSystemMeta";
import type { DashboardVersionStatus } from "../../hooks/useSystemMeta";
import { useMe } from "../../hooks/useMe";
import { isAdminUser } from "../../lib/permissions";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { cn } from "../../utils/cn";
import { Tooltip } from "../ui/Tooltip";
import { Popover } from "../ui/Popover";
import { Icon } from "../ui/Icon";
import { Badge } from "../ui/Badge";
import { DashboardUpdateModal } from "./DashboardUpdateModal";
import { QbMaintenanceModal } from "../update/QbMaintenanceModal";

const DOT_SIZE = "h-3 w-3";

const toneClasses = {
  healthy: {
    dot: "bg-emerald-500 shadow-[0_0_0_6px_rgba(16,185,129,0.18)]",
  },
  update: {
    dot: "bg-amber-500 shadow-[0_0_0_6px_rgba(245,158,11,0.2)]",
  },
  degraded: {
    dot: "bg-rose-500 shadow-[0_0_0_6px_rgba(244,63,94,0.2)]",
  },
} as const;

const formatVersion = (label: string, value: string | null | undefined) => (
  <div className="flex items-center justify-between gap-2 text-sm text-foreground">
    <span className="text-muted-foreground">{label}</span>
    <code className="rounded bg-muted/60 px-1.5 py-[2px] font-mono text-[11px] leading-tight text-foreground/90">
      {value ?? "—"}
    </code>
  </div>
);

const StatusLine = ({
  updateAvailable,
  installed,
  latest,
  health,
  message,
}: {
  updateAvailable: boolean;
  installed: string | null;
  latest: string | null;
  health: "healthy" | "degraded" | "down";
  message?: string;
}) => {
  if (updateAvailable && latest) {
    return (
      <div className="flex items-start justify-between gap-2 text-sm text-amber-600 dark:text-amber-400">
        <span className="font-medium">Update available</span>
        <span className="text-right text-xs text-muted-foreground">{installed ?? "?"} → {latest}</span>
      </div>
    );
  }

  if (health === "degraded" || health === "down") {
    return (
      <div className="flex items-start gap-2 text-sm text-rose-600 dark:text-rose-400">
        <Icon icon="lucide:alert-circle" className="mt-[2px] h-4 w-4" />
        <span>{message ?? "Status degraded"}</span>
      </div>
    );
  }

  return <div className="text-sm text-emerald-600 dark:text-emerald-400">Up to date</div>;
};

const dashboardBadgeConfig: Record<DashboardVersionStatus, { label: string; variant: "success" | "warning" | "muted" }> = {
  up_to_date: { label: "dashboard up-to-date", variant: "success" },
  update_available: { label: "update available", variant: "warning" },
  not_configured: { label: "not configured", variant: "muted" },
  unavailable: { label: "unavailable", variant: "muted" },
};

const DashboardStatusBadge = ({ status }: { status: DashboardVersionStatus }) => {
  const config = dashboardBadgeConfig[status];
  return (
    <Badge variant={config.variant} size="xs">
      {config.label}
    </Badge>
  );
};

export const SystemStatusDot = () => {
  const { data, error, loading } = useSystemMeta({ refreshMs: 60_000 });
  const { user } = useMe();
  const isAdmin = isAdminUser(user);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [qbUpdateModalOpen, setQbUpdateModalOpen] = useState(false);
  const openLabel = "System status — click for details";

  const visualTone: "healthy" | "update" | "degraded" = useMemo(() => {
    if (error) return "degraded";
    if (data?.health === "down" || data?.health === "degraded") return "degraded";
    if (data?.updateAvailable || data?.dashboardVersion?.status === "update_available") return "update";
    return "healthy";
  }, [data?.health, data?.updateAvailable, data?.dashboardVersion?.status, error]);

  const installedVersion = data?.installedVersion ?? null;
  const latestVersion = data?.latestVersion ?? null;
  const dashboardUpdateAvailable = data?.dashboardVersion?.status === "update_available";

  const popoverContent = (close: () => void) => (
    <div className="min-w-[260px] max-w-[320px] space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-foreground">System status</div>
          <div className="text-xs text-muted-foreground">Updated {data?.updatedAt ?? "—"}</div>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
        >
          <Icon icon="lucide:x" className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-2">
        {formatVersion("Installed", installedVersion)}
        {formatVersion("Latest", latestVersion)}
        <StatusLine
          updateAvailable={Boolean(data?.updateAvailable)}
          installed={installedVersion}
          latest={latestVersion}
          health={data?.health ?? "degraded"}
          message={data?.message ?? error}
        />

        {/* Update QuickBox button — admin only, shown when update available */}
        {isAdmin && data?.updateAvailable && (
          <button
            type="button"
            onClick={() => {
              close();
              setQbUpdateModalOpen(true);
            }}
            className="w-full mt-1 flex items-center justify-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-400 transition-colors hover:bg-amber-500/20"
          >
            <Icon icon="lucide:download" className="h-3.5 w-3.5" />
            Update QuickBox
          </button>
        )}
      </div>

      {data?.dashboardVersion && (
        <div className="space-y-2 border-t border-border/40 pt-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Dashboard
            </div>
            <DashboardStatusBadge status={data.dashboardVersion.status} />
          </div>
          {formatVersion("Current", data.dashboardVersion.currentVersion !== "unknown"
            ? `v${data.dashboardVersion.currentVersion}` : data.dashboardVersion.currentCommitShort)}
          {formatVersion("Latest", data.dashboardVersion.latestVersion
            ? `v${data.dashboardVersion.latestVersion}` : data.dashboardVersion.latestCommitShort)}

          {data.dashboardVersion.status === "unavailable" && data.dashboardVersion.error && (
            <div className="text-[10px] leading-tight text-muted-foreground/70 line-clamp-2">
              {data.dashboardVersion.error}
            </div>
          )}

          {/* Update button — admin only, shown when update available */}
          {isAdmin && dashboardUpdateAvailable && (
            <button
              type="button"
              onClick={() => {
                close();
                setUpdateModalOpen(true);
              }}
              className="w-full mt-1 flex items-center justify-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-400 transition-colors hover:bg-amber-500/20"
            >
              <Icon icon="lucide:download" className="h-3.5 w-3.5" />
              Update dashboard
            </button>
          )}
        </div>
      )}

      {isAdmin ? (
        <div className="grid grid-cols-1 gap-2 text-sm">
          <NavLink
            to="/system/changelog"
            className="flex items-center justify-between rounded-md border border-border/60 bg-muted/70 px-3 py-2 text-foreground transition-colors hover:bg-muted"
            onClick={close}
          >
            <span>Changelog</span>
            <Icon icon="lucide:chevron-right" className="h-4 w-4 text-muted-foreground" />
          </NavLink>
          <NavLink
            to="/system/dashboard"
            className="flex items-center justify-between rounded-md border border-border/60 bg-muted/70 px-3 py-2 text-foreground transition-colors hover:bg-muted"
            onClick={close}
          >
            <span>System Dashboard</span>
            <Icon icon="lucide:chevron-right" className="h-4 w-4 text-muted-foreground" />
          </NavLink>
          <NavLink
            to="/system/logs"
            className="flex items-center justify-between rounded-md border border-border/60 bg-muted/70 px-3 py-2 text-foreground transition-colors hover:bg-muted"
            onClick={close}
          >
            <span>View system logs</span>
            <Icon icon="lucide:chevron-right" className="h-4 w-4 text-muted-foreground" />
          </NavLink>
        </div>
      ) : null}
    </div>
  );

  const tone = toneClasses[visualTone];
  const pulseClass = prefersReducedMotion ? "" : "animate-pulse";

  return (
    <div className="mt-auto w-full pb-2">
      <div className="flex items-center justify-center">
        <Tooltip
          content={openLabel}
          side="right"
          align="center"
          disabled={popoverOpen}
          panelClassName="min-w-[220px] max-w-[260px] text-left"
        >
          <Popover
            side="top"
            align="center"
            onOpenChange={setPopoverOpen}
            trigger={
              <button
                type="button"
                aria-label="System status"
                className="flex items-center justify-center"
              >
                <span
                  className={cn(
                    DOT_SIZE,
                    "rounded-full",
                    tone.dot,
                    pulseClass,
                    loading ? "opacity-80" : ""
                  )}
                />
              </button>
            }
            renderContent={popoverContent}
            panelClassName="rounded-xl border border-border/60 bg-card/95 p-4 shadow-2xl backdrop-blur"
          />
        </Tooltip>
      </div>

      {/* Dashboard update modal — rendered outside the popover */}
      <DashboardUpdateModal
        open={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        currentCommitShort={data?.dashboardVersion?.currentCommitShort ?? null}
        latestCommitShort={data?.dashboardVersion?.latestCommitShort ?? null}
        currentVersion={data?.dashboardVersion?.currentVersion ?? null}
        latestVersion={data?.dashboardVersion?.latestVersion ?? null}
        channel={data?.dashboardVersion?.channel}
      />

      {/* QuickBox update modal — rendered outside the popover */}
      <QbMaintenanceModal
        open={qbUpdateModalOpen}
        onClose={() => setQbUpdateModalOpen(false)}
        initialAction="update_quickbox"
        skipConfirmation
      />
    </div>
  );
};
